/* eslint-disable react/display-name */
import React, { useState, Component } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from 'react-data-table-component'
import orderBy from 'lodash/orderBy'
import RiderComponent from '../components/Rider/Rider'
import { getRiders, deleteRider, toggleAvailablity, getAvailableRiders } from '../apollo'
import {
  Box, Container, Typography, Paper, TextField, InputAdornment,
  IconButton, Menu, MenuItem, ListItemIcon, Button, Dialog,
  DialogTitle, DialogContent, Switch, Chip
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import CloseIcon from '@mui/icons-material/Close'
import StorefrontIcon from '@mui/icons-material/Storefront'

const GET_RIDERS = gql`${getRiders}`
const DELETE_RIDER = gql`${deleteRider}`
const TOGGLE_RIDER = gql`${toggleAvailablity}`
const GET_AVAILABLE_RIDERS = gql`${getAvailableRiders}`

class RidersErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null } }
  static getDerivedStateFromError(error) { return { hasError: true, error } }
  render() {
    if (this.state.hasError) return (
      <Box sx={{ p: 3, color: '#dc2626', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', m: 3 }}>
        <strong>Riders page error:</strong> {this.state.error?.message || 'Unknown error'}
      </Box>
    )
    return this.props.children
  }
}

function Riders(props) {
  const { t } = props
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [selectedRider, setSelectedRider] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const [mutateToggle] = useMutation(TOGGLE_RIDER, { refetchQueries: [{ query: GET_RIDERS }, { query: GET_AVAILABLE_RIDERS }] })
  const [mutateDelete] = useMutation(DELETE_RIDER, { refetchQueries: [{ query: GET_RIDERS }] })
  const { data, error, loading, refetch } = useQuery(GET_RIDERS)

  const allRiders = data?.riders ?? []
  const regex = searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered = searchQuery.length < 3 ? allRiders : allRiders.filter(r =>
    r.name?.toLowerCase().search(regex) > -1 ||
    r.username?.toLowerCase().search(regex) > -1 ||
    r.phone?.toLowerCase().search(regex) > -1 ||
    r.restaurantName?.toLowerCase().search(regex) > -1
  )

  const availableCount = allRiders.filter(r => r.available).length

  const ActionMenu = ({ row }) => {
    const [anchor, setAnchor] = useState(null)
    return (
      <>
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#f1f5f9' } }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
          PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 160 } }}>
          <MenuItem onClick={() => { setAnchor(null); setSelectedRider(row); setEditModal(true) }} sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem' }}>
            <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>Edit
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); mutateDelete({ variables: { id: row._id } }) }} sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem', color: '#ef4444' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>Delete
          </MenuItem>
        </Menu>
      </>
    )
  }

  const columns = [
    {
      name: 'RIDER', sortable: true, selector: row => row.name, minWidth: '200px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#eff6ff', border: '2px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TwoWheelerIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{row.name}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{row.username}</Typography>
          </Box>
        </Box>
      )
    },
    {
      name: 'PHONE', sortable: true, selector: row => row.phone, minWidth: '140px', hide: 'sm',
      cell: row => <Typography sx={{ fontSize: '0.83rem', color: '#64748b' }}>{row.phone || '—'}</Typography>
    },
    {
      name: 'TYPE', minWidth: '190px', hide: 'md',
      cell: row => {
        const isRestaurant = row.driverType === 'RESTAURANT'
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Chip
              icon={isRestaurant
                ? <StorefrontIcon sx={{ fontSize: '13px !important', color: '#9333ea !important' }} />
                : <TwoWheelerIcon sx={{ fontSize: '13px !important', color: '#2563eb !important' }} />}
              label={isRestaurant ? 'Restaurant Driver' : 'RushMyFood Driver'}
              size="small"
              sx={{
                width: 'fit-content',
                bgcolor: isRestaurant ? '#faf5ff' : '#eff6ff',
                color: isRestaurant ? '#9333ea' : '#2563eb',
                border: `1px solid ${isRestaurant ? '#e9d5ff' : '#bfdbfe'}`,
                fontWeight: 600, fontSize: '0.68rem', height: 22,
                '& .MuiChip-icon': { ml: '6px' }
              }} />
            {isRestaurant && row.restaurantName && (
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', ml: 0.5 }}>{row.restaurantName}</Typography>
            )}
          </Box>
        )
      }
    },
    {
      name: 'AVAILABLE', minWidth: '120px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch size="small" defaultChecked={row.available}
            onChange={() => mutateToggle({ variables: { id: row._id } })}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }} />
          <Chip label={row.available ? 'Online' : 'Offline'} size="small"
            sx={{ bgcolor: row.available ? '#f0fdf4' : '#f8fafc', color: row.available ? '#166534' : '#64748b', border: `1px solid ${row.available ? '#bbf7d0' : '#e2e8f0'}`, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
        </Box>
      )
    },
    { name: '', right: true, width: '60px', cell: row => <ActionMenu row={row} /> }
  ]

  return (
    <RidersErrorBoundary>
      <>
        <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
          <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TwoWheelerIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Riders</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage delivery riders and their availability</Typography>
              </Box>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedRider(null); setAddModal(true) }}
              sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontWeight: 700, px: 3, py: 1.2, borderRadius: '12px', boxShadow: '0 4px 16px rgba(59,130,246,0.45)', textTransform: 'none', '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
              Add Rider
            </Button>
          </Box>
        </Box>

        <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            {[
              { label: 'Total Riders', value: allRiders.length, bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '#bfdbfe', color: '#1e40af' },
              { label: 'Available Now', value: availableCount, bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '#bbf7d0', color: '#166534' },
              { label: 'Offline', value: allRiders.length - availableCount, bg: 'linear-gradient(135deg,#f8fafc,#f1f5f9)', border: '#e2e8f0', color: '#64748b' }
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '16px', px: 3, py: 2, minWidth: 140, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{s.value}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{s.label}</Typography>
              </Paper>
            ))}
          </Box>

          <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                All Riders <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>({filtered.length})</Typography>
              </Typography>
              <TextField size="small" placeholder="Search riders..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px', fontSize: '0.83rem', bgcolor: '#fff' } }}
                sx={{ width: 260 }} />
            </Box>
            {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}
            {loading ? <CustomLoader /> : (
              <DataTable columns={columns} data={filtered} pagination noHeader
                sortFunction={(rows, field, dir) => orderBy(rows, row => row[field]?.toString().toLowerCase(), dir)}
                customStyles={{ headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', minHeight: '44px' } }, headCells: { style: { fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' } }, rows: { style: { borderBottom: '1px solid #f1f5f9', minHeight: '64px', '&:hover': { backgroundColor: '#f8fafc' } } }, cells: { style: { paddingLeft: '16px', paddingRight: '16px' } } }}
                noDataComponent={<Box py={8} textAlign="center"><TwoWheelerIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} /><Typography sx={{ color: '#64748b' }}>No riders found</Typography></Box>}
              />
            )}
          </Paper>
        </Container>

        <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
            <Typography fontWeight={700} fontSize="1rem">Add New Rider</Typography>
            <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 3 }}>
            <RiderComponent onClose={() => { setAddModal(false); refetch() }} />
          </DialogContent>
        </Dialog>

        <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
            <Typography fontWeight={700} fontSize="1rem">Edit Rider</Typography>
            <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 3 }}>
            <RiderComponent rider={selectedRider} onClose={() => setEditModal(false)} />
          </DialogContent>
        </Dialog>
      </>
    </RidersErrorBoundary>
  )
}

export default withTranslation()(Riders)
